import { Component } from '@theme/component';
import { entryFromItem, rankEntries, searchTerms } from '@theme/faq-search';

/**
 * FAQ list with category pills, a question search and a "see all" expander.
 * Every question is server-rendered; this only toggles classes and reports
 * interactions to PostHog under `data-analytics-prefix`.
 *
 * Search logic lives in @theme/faq-search (shared with the Kundenservice
 * hero). Results are ranked via CSS `order`, so the DOM itself never moves.
 *
 * Other sections can open it with
 * `document.dispatchEvent(new CustomEvent('customer-faq:open', { detail }))`,
 * where detail is one of `{ topic }` (a `faq_fragen_themenfelder` handle),
 * `{ search }` (a query) or `{ item }` (an item's `data-faq-id`).
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} [searchInput]
 * @property {HTMLButtonElement[]} [pills]
 * @property {HTMLElement[]} [categories]
 * @property {HTMLElement[]} [items]
 * @property {HTMLElement} [list]
 * @property {HTMLButtonElement} [seeAll]
 * @property {HTMLElement} [empty]
 *
 * @extends {Component<Refs>}
 */
class CustomerFaqComponent extends Component {
  #searchTimer = 0;
  #analyticsDebounce = 0;
  /** @type {WeakMap<HTMLElement, import('@theme/faq-search').SearchEntry>} */
  #entries = new WeakMap();

  get analyticsPrefix() {
    return this.dataset.analyticsPrefix || 'pdp';
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('customer-faq:open', this.#handleExternalOpen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('customer-faq:open', this.#handleExternalOpen);
  }

  /** @param {Event} event */
  #handleExternalOpen = (event) => {
    const { topic = '', search = '', item = '' } = /** @type {CustomEvent} */ (event).detail ?? {};
    const input = this.refs.searchInput;
    window.clearTimeout(this.#searchTimer);

    if (item) {
      const target = (this.refs.items ?? []).find((i) => i.dataset.faqId === item);
      const category = target?.closest('.customer-faq__category');
      if (!(target instanceof HTMLElement) || !(category instanceof HTMLElement)) return;
      if (input instanceof HTMLInputElement) input.value = '';
      this.#activateCategory(category.dataset.categoryId ?? '');
      if (target.dataset.overflow === 'true') this.dataset.expanded = 'true';
      const details = target.querySelector('details');
      if (details) details.open = true;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (topic) {
      const category = (this.refs.categories ?? []).find((c) => c.dataset.topic === topic);
      if (!category) return;
      if (input instanceof HTMLInputElement) input.value = '';
      this.#activateCategory(category.dataset.categoryId ?? '');
    } else if (search && input instanceof HTMLInputElement) {
      input.value = search;
      this.#applySearch(search);
      this.#openFirstMatch();
    } else {
      return;
    }

    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  setActiveCategory = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;
    const categoryId = button.dataset.categoryId;
    if (!categoryId) return;

    const wasAlreadyActive = button.classList.contains('customer-faq__pill--active');

    const input = this.refs.searchInput;
    if (input instanceof HTMLInputElement && input.value !== '') {
      input.value = '';
    }
    window.clearTimeout(this.#searchTimer);

    this.#activateCategory(categoryId);

    if (!wasAlreadyActive) {
      window.phCapture?.(`${this.analyticsPrefix}_faq_category_selected`, {
        category_id: categoryId,
        category_label: button.textContent.trim(),
      });
    }
  };

  handleSearch = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    window.clearTimeout(this.#searchTimer);
    this.#searchTimer = window.setTimeout(() => this.#applySearch(input.value), 120);
    // Separate, longer debounce so analytics fires once per settled query.
    window.clearTimeout(this.#analyticsDebounce);
    this.#analyticsDebounce = window.setTimeout(() => this.#captureSearch(), 800);
  };

  expandAll = () => {
    this.dataset.expanded = 'true';
    window.phCapture?.(`${this.analyticsPrefix}_faq_expand_all_clicked`);
  };

  questionClicked = (event) => {
    const summary = event.target.closest('.customer-faq__item-summary');
    const details = summary?.closest('details');
    // Native click toggles details *after* this handler runs; capture only opens.
    if (!details || details.open) return;
    const question = summary.querySelector('.customer-faq__item-question')?.textContent.trim() || '';
    const category = summary.closest('.customer-faq__category');
    const categoryId = category?.dataset.categoryId || null;
    const categoryLabel =
      (this.refs.pills ?? []).find((p) => p.dataset.categoryId === categoryId)?.textContent.trim() || null;
    window.phCapture?.(`${this.analyticsPrefix}_faq_question_opened`, {
      question,
      category_id: categoryId,
      category_label: categoryLabel,
    });
  };

  #captureSearch() {
    const input = this.refs.searchInput;
    const raw = input instanceof HTMLInputElement ? input.value.trim() : '';
    if (!raw) return;
    const resultCount = this.querySelectorAll(
      '.customer-faq__item-wrap:not(.customer-faq__item-wrap--hidden)'
    ).length;
    window.phCapture?.(`${this.analyticsPrefix}_faq_search_performed`, {
      query: raw,
      result_count: resultCount,
      had_results: resultCount > 0,
    });
  }

  #activateCategory(categoryId) {
    this.dataset.expanded = 'false';
    this.dataset.searching = 'false';

    for (const pill of this.refs.pills ?? []) {
      const active = pill.dataset.categoryId === categoryId;
      pill.classList.toggle('customer-faq__pill--active', active);
      pill.setAttribute('aria-pressed', active ? 'true' : 'false');
    }

    for (const category of this.refs.categories ?? []) {
      category.classList.toggle('customer-faq__category--hidden', category.dataset.categoryId !== categoryId);
      category.classList.remove('customer-faq__category--no-matches');
      category.style.order = '';
    }

    for (const item of this.refs.items ?? []) {
      item.classList.remove('customer-faq__item-wrap--hidden');
      item.style.order = '';
    }

    if (this.refs.empty) this.refs.empty.hidden = true;
  }

  #applySearch(rawValue) {
    const terms = searchTerms(rawValue);

    if (terms.length === 0) {
      const activePill = (this.refs.pills ?? []).find((p) => p.classList.contains('customer-faq__pill--active'));
      const activeId = activePill?.dataset.categoryId ?? '';
      this.#activateCategory(activeId);
      return;
    }

    this.dataset.searching = 'true';

    const items = this.refs.items ?? [];
    const results = rankEntries(items.map((item) => this.#entry(item)), terms);

    const bestPerCategory = new Map();
    let total = 0;
    items.forEach((item, index) => {
      const { matches, score } = results[index];
      item.classList.toggle('customer-faq__item-wrap--hidden', !matches);
      item.style.order = matches ? String(-score) : '';
      if (matches) total++;

      const parent = item.closest('.customer-faq__category');
      if (parent instanceof HTMLElement) {
        const id = parent.dataset.categoryId ?? '';
        bestPerCategory.set(id, Math.max(bestPerCategory.get(id) ?? 0, matches ? score : 0));
      }
    });

    for (const category of this.refs.categories ?? []) {
      const best = bestPerCategory.get(category.dataset.categoryId ?? '') ?? 0;
      category.classList.toggle('customer-faq__category--no-matches', best === 0);
      category.style.order = best > 0 ? String(-best) : '';
    }

    if (this.refs.empty) this.refs.empty.hidden = total > 0;
  }

  /** @param {HTMLElement} item */
  #entry(item) {
    let entry = this.#entries.get(item);
    if (!entry) {
      entry = entryFromItem(item);
      this.#entries.set(item, entry);
    }
    return entry;
  }

  #openFirstMatch() {
    const visible = (this.refs.items ?? []).filter((item) => !item.classList.contains('customer-faq__item-wrap--hidden'));
    const rank = (item) => Number(item.closest('.customer-faq__category')?.style.order || 0) * 1000 + Number(item.style.order || 0);
    const first = visible.sort((a, b) => rank(a) - rank(b))[0];
    const details = first?.querySelector('details');
    if (details) details.open = true;
  }
}

if (!customElements.get('customer-faq-component')) {
  customElements.define('customer-faq-component', CustomerFaqComponent);
}
